import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Paper,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { IoChevronDownOutline } from 'react-icons/io5';
import Swal from 'sweetalert2';
import axios from 'axios';
import AppDatatable from '../../components/AppDatatable';
import AccessDeniedState from '../../components/AccessDeniedState';
import { createLocalId, deleteLocalItem, normalizeStatus, readLocalCollection, upsertLocalItem } from '../../utils/localData';
import { getLoggedInUser, hasModulePermission } from '../../utils/permissions';
import { getModuleDisplayName } from '../../utils/modules';

const STORAGE_KEY = 'ra_access_roles_local';
const ACTION_KEYS = ['View', 'Create', 'Edit', 'Update', 'Delete'];
const DOCUMENT_EDIT_ACTION = 'Document Edit';
const ACCESS_PERMISSION_KEYS = ['Access Setup', 'Access'];
const permissionKey = (parentName, childName = '') => childName ? `${parentName} / ${childName}` : parentName;

const buildModuleGroups = (apiModules = []) => (Array.isArray(apiModules) ? apiModules : []).map((moduleItem) => {
  const name = moduleItem?.name || moduleItem?.routing || 'Untitled Module';
  const children = (Array.isArray(moduleItem?.sub_module) ? moduleItem.sub_module : []).map((subModule) => {
    const childName = subModule?.sub_name || subModule?.name || subModule?.routing || 'Untitled Page';
    return { name: childName, key: permissionKey(name, childName), routing: subModule?.routing || '' };
  });
  return { name: getModuleDisplayName(name), key: permissionKey(name), children };
});
const supportsDocumentEdit = (group, child) => (
  /vehicle|shuttle|windshield|tire/i.test(group?.name || '')
  && /submi(?:t|te)d forms|closed forms?/i.test(child?.name || '')
);

const createInitialForm = () => ({
  _id: '',
  role_name: '',
  status: 'Active',
  accessRole: {},
});

export default function AccessSetupPage() {
  const [rows, setRows] = useState([]);
  const [moduleGroups, setModuleGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(createInitialForm());
  const [error, setError] = useState('');
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const currentUser = getLoggedInUser();
  const currentUserName = currentUser?.userName || '';
  const accessRole = currentUser?.accessRole || {};
  const canView = hasModulePermission(ACCESS_PERMISSION_KEYS, 'View', accessRole);
  const canCreate = hasModulePermission(ACCESS_PERMISSION_KEYS, 'Create', accessRole);
  const canEdit = hasModulePermission(ACCESS_PERMISSION_KEYS, 'Edit', accessRole);
  const canDelete = hasModulePermission(ACCESS_PERMISSION_KEYS, 'Delete', accessRole);

  const buildAccessTemplate = (moduleNames, existing = {}) =>
    [...new Set([...(moduleNames || []), ...Object.keys(existing || {})])].reduce((acc, moduleName) => {
      acc[moduleName] = [...ACTION_KEYS, DOCUMENT_EDIT_ACTION].reduce((actionAcc, actionKey) => {
        actionAcc[actionKey] = Boolean(existing?.[moduleName]?.[actionKey]);
        return actionAcc;
      }, {});
      return acc;
    }, {});

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [modulesResponse, rolesResponse] = await Promise.allSettled([
        axios.get(`${baseURL}/get_modules`),
        axios.get(`${baseURL}/get_access_roles`),
      ]);

      const dynamicGroups = modulesResponse.status === 'fulfilled'
        ? buildModuleGroups(modulesResponse.value.data)
        : [];
      setModuleGroups(dynamicGroups);

      const apiRoles = rolesResponse.status === 'fulfilled' && Array.isArray(rolesResponse.value?.data)
        ? rolesResponse.value.data
        : [];
      const localRoles = readLocalCollection(STORAGE_KEY);
      const merged = [...apiRoles];

      localRoles.forEach((role) => {
        if (!merged.some((item) => String(item?._id || item?.role_name) === String(role?._id || role?.role_name))) {
          merged.push(role);
        }
      });

      setRows(merged);
    } catch {
      setRows(readLocalCollection(STORAGE_KEY));
      setError('Access roles API is not available, so local data is being shown.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setForm(createInitialForm());
    setOpenDialog(false);
    setSubmitting(false);
  };

  const openCreate = () => {
    if (!canCreate) return;
    setForm({
      _id: '',
      role_name: '',
      status: 'Active',
      accessRole: buildAccessTemplate(moduleGroups.flatMap((group) => [group.key, ...group.children.map((child) => child.key)])),
    });
    setOpenDialog(true);
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    setForm({
      _id: row?._id || '',
      role_name: row?.role_name || row?.selected_role || '',
      status: row?.status || 'Active',
      accessRole: buildAccessTemplate(moduleGroups.flatMap((group) => [group.key, ...group.children.map((child) => child.key)]), row?.accessRole),
    });
    setOpenDialog(true);
  };

  const handleFieldChange = ({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (moduleName, actionKey, checked) => {
    setForm((prev) => ({
      ...prev,
      accessRole: {
        ...prev.accessRole,
        [moduleName]: {
          ...prev.accessRole[moduleName],
          [actionKey]: checked,
        },
      },
    }));
  };

  const handleGroupPermissionChange = (group, actionKey, checked) => {
    const keys = [group.key, ...group.children.map((child) => child.key)];
    setForm((prev) => ({
      ...prev,
      accessRole: keys.reduce((next, key) => ({
        ...next,
        [key]: { ...next[key], [actionKey]: checked },
      }), { ...prev.accessRole }),
    }));
  };

  const isGroupActionEnabled = (group, actionKey) => (
    [group.key, ...group.children.map((child) => child.key)]
      .every((key) => Boolean(form.accessRole?.[key]?.[actionKey]))
  );

  const handleSubmit = async () => {
    if ((!form._id && !canCreate) || (form._id && !canEdit)) {
      await Swal.fire({
        icon: 'warning',
        text: form._id ? 'You do not have permission to edit access roles.' : 'You do not have permission to create access roles.',
      });
      return;
    }

    if (!form.role_name.trim()) {
      Swal.fire({ icon: 'warning', text: 'Role name is required.' });
      return;
    }

    const payload = {
      ...form,
      _id: form._id || createLocalId('role'),
      ...(form._id ? { updated_by: currentUserName } : { created_by: currentUserName }),
    };

    setSubmitting(true);
    try {
      let response;

      if (form._id) {
        response = await axios.put(`${baseURL}/update_access_role/${form._id}`, payload);
      } else {
        response = await axios.post(`${baseURL}/create_access_role`, payload);
      }

      const result = response?.data || {};

      if (result.status_code === 1) {
        await Swal.fire({
          icon: 'success',
          text: result.message || (form._id ? 'Access role updated successfully' : 'Access role created successfully'),
        });
        await fetchData();
        resetForm();
        return;
      }

      await Swal.fire({
        icon: 'error',
        text: result.message || 'Unable to save access role.',
      });
    } catch {
      upsertLocalItem(STORAGE_KEY, payload);
      await fetchData();
      await Swal.fire({
        icon: 'success',
        text: form._id ? 'Access role updated locally.' : 'Access role created locally.',
      });
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete) {
      await Swal.fire({
        icon: 'warning',
        text: 'You do not have permission to delete access roles.',
      });
      return;
    }

    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete access role?',
      text: `Remove ${row.role_name}?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#c62828',
    });

    if (!confirmation.isConfirmed) return;

    try {
      const response = await axios.delete(`${baseURL}/delete_access_role/${row._id}`, {
        data: { deleted_by: currentUserName },
      });

      const result = response?.data || {};

      if (result.status_code === 1) {
        await Swal.fire({
          icon: 'success',
          text: result.message || 'Access role deleted successfully',
        });
        await fetchData();
        return;
      }

      await Swal.fire({
        icon: 'error',
        text: result.message || 'Unable to delete access role.',
      });
    } catch {
      deleteLocalItem(STORAGE_KEY, row._id);
      await fetchData();
      await Swal.fire({
        icon: 'success',
        text: 'Access role deleted locally.',
      });
    }
  };

  const tableData = useMemo(() => ({
    columns: [
      { label: 'Role Name', field: 'role_name' },
      { label: 'Status', field: 'status' },
      { label: 'Modules', field: 'modules' },
      { label: 'Action', field: 'action', sort: false },
    ],
    rows: rows.map((row) => ({
      role_name: row.role_name || row.selected_role || '-',
      status: normalizeStatus((row.status || 'Active') === 'Active'),
      modules: Object.keys(row.accessRole || {}).filter((key) => row.accessRole?.[key]?.View).join(', ') || '-',
      action: (
        <Stack direction="row" spacing={1}>
          {canEdit ? <Button size="small" variant="outlined" onClick={() => openEdit(row)}>Edit</Button> : null}
          {canDelete ? <Button size="small" color="error" variant="contained" onClick={() => handleDelete(row)}>Delete</Button> : null}
          {!canEdit && !canDelete ? '-' : null}
        </Stack>
      ),
    })),
  }), [canDelete, canEdit, rows]);

  if (!canView) {
    return <AccessDeniedState message="You do not have permission to view access setup." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 } }}>
      <AppDatatable
        title="Access Roles"
        subtitle="Each role controls which pages and actions a user can access."
        headerActions={canCreate ? <Button variant="contained" onClick={openCreate}>Create Access Role</Button> : null}
        headerContent={error ? <Typography sx={{ color: 'warning.main' }}>{error}</Typography> : null}
        data={tableData}
        loading={loading}
        noFoundMessage="No access roles created yet."
      />

      <Dialog open={openDialog} onClose={resetForm} fullWidth maxWidth="lg">
        <DialogTitle>{form._id ? 'Edit Access Role' : 'Create Access Role'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
            <Grid item xs={12} md={8}>
              <TextField label="Role Name" name="role_name" value={form.role_name} onChange={handleFieldChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" name="status" value={form.status} onChange={handleFieldChange}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }}>
            <Typography fontWeight={700}>Permission guide</Typography>
            <Typography variant="body2" color="text.secondary">
              Enable View and Document Edit on Submitted Forms (Claims Processing) or Closed Forms to allow document uploads and deletion without allowing claim-field edits.
            </Typography>
          </Paper>

          {moduleGroups.map((group) => (
            <Accordion key={group.key} disableGutters defaultExpanded={group.children.length > 0} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '12px !important', overflow: 'hidden', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<IoChevronDownOutline />}>
                <Box sx={{ width: '100%', pr: 2 }}>
                  <Typography fontWeight={700}>{group.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{group.children.length ? `${group.children.length} pages` : 'Standalone module'}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'primary.50' }}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} md={4}><Typography fontWeight={700}>Entire module</Typography></Grid>
                    {ACTION_KEYS.map((actionKey) => (
                      <Grid item xs={6} sm={3} md={2} key={`${group.key}-${actionKey}`}>
                        <FormControlLabel
                          control={<Switch checked={isGroupActionEnabled(group, actionKey)} onChange={(event) => handleGroupPermissionChange(group, actionKey, event.target.checked)} />}
                          label={actionKey}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                {group.children.map((child) => (
                  <React.Fragment key={child.key}>
                    <Divider />
                    <Grid container alignItems="center" spacing={1} sx={{ px: 1.5, py: 1 }}>
                      <Grid item xs={12} md={4}>
                        <Typography fontWeight={600}>{child.name}</Typography>
                        {child.routing ? <Typography variant="caption" color="text.secondary">/{String(child.routing).replace(/^\//, '')}</Typography> : null}
                      </Grid>
                      {[...ACTION_KEYS, ...(supportsDocumentEdit(group, child) ? [DOCUMENT_EDIT_ACTION] : [])].map((actionKey) => (
                        <Grid item xs={6} sm={3} md={2} key={`${child.key}-${actionKey}`}>
                          <FormControlLabel
                            control={<Switch checked={Boolean(form.accessRole?.[child.key]?.[actionKey])} onChange={(event) => handlePermissionChange(child.key, actionKey, event.target.checked)} />}
                            label={actionKey}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </React.Fragment>
                ))}

              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={resetForm}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
